import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "../components/SignaturePad";

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

// Same basename logic as App.js — deployed under /talentpool in production.
const BASENAME = window.__RUNTIME_CONFIG__?.apiBase?.startsWith("/talentpool") ? "/talentpool" : "";

const tok = () => localStorage.getItem("applicantToken") || "";

// ── shared styling — matches ApplicantPortal.jsx's candidate-portal look ──
const BRAND = {
  primary: "#9c27b0",
  primaryBg: "#f3e5f7",
  white: "#ffffff",
  text: "#1a1a1a",
  textLight: "#666",
  border: "#e0d4e8",
  bg: "#faf5fc",
  green: "#2e7d32",
  greenBg: "#f1f8e9",
  red: "#b71c1c",
  redBg: "#ffebee",
};

const inp = {
  padding: "10px 12px",
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};

const lbl = {
  display: "flex",
  flexDirection: "column",
  fontSize: 13,
  color: BRAND.textLight,
  gap: 5,
  marginBottom: 12,
};

const gridTwo = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };

const btnP = (disabled) => ({
  padding: "12px 24px",
  background: disabled ? "#ccc" : BRAND.primary,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: 700,
  fontSize: 15,
  fontFamily: "inherit",
});

const btnGhost = {
  padding: "10px 18px",
  background: "#fff",
  color: BRAND.primary,
  border: `1px solid ${BRAND.border}`,
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
  fontFamily: "inherit",
};

const card = { background: "#fff", borderRadius: 14, padding: 20, marginBottom: 16, border: `1px solid ${BRAND.border}` };
const cardTitle = { margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: BRAND.text };

// Every candidate-editable field on TeamMemberInfo — mirrors
// TMI_CANDIDATE_FIELDS in backend/routes/applicants.py exactly.
const TMI_FIELDS = [
  "surname", "first_names", "known_as", "second_name", "personal_title", "initials", "language",
  "residential_address", "postal_code", "date_of_birth", "gender", "race", "home_telephone",
  "cell_phone", "income_tax_number", "has_drivers_license", "drivers_license_code", "personal_email",
  "doctor_name", "doctor_telephone", "allergies_medical_conditions", "emergency_contact_name",
  "emergency_contact_telephone", "next_of_kin_name", "next_of_kin_relationship", "next_of_kin_address",
  "next_of_kin_postal_code", "next_of_kin_home_telephone", "next_of_kin_cell_phone", "is_sa_citizen",
  "work_permit_number", "work_permit_expiry_date", "bank_name", "bank_branch", "bank_account_number",
  "bank_branch_code", "bank_account_holder", "bank_account_type",
];

const emptyForm = () => TMI_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {});

const SUPPORTING_DOCS = [
  { type: "id", label: "Identity Document" },
  { type: "drivers_licence", label: "Valid Driver's Licence" },
  { type: "hpcsa", label: "HPCSA Certificate / Registration" },
  { type: "degree", label: "Degree / Diploma" },
  { type: "banking_details", label: "Proof of Bank Account" },
];

function boolSelectValue(v) {
  return v === true ? "yes" : v === false ? "no" : "";
}

export default function TeamMemberInfoForm() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [onboardingStage, setOnboardingStage] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const formRef = useRef(emptyForm());

  const [uploadedDocs, setUploadedDocs] = useState({});
  const [docFilenames, setDocFilenames] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});

  const [saveMsg, setSaveMsg] = useState("");
  const saveTimerRef = useRef(null);

  const [declareChecked, setDeclareChecked] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);

  const loggedIn = !!tok();

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [tmiRes, docsRes] = await Promise.all([
        fetch(`${API}/api/applicants/team-member-info`, { headers: { Authorization: `Bearer ${tok()}` } }),
        fetch(`${API}/api/applicants/documents`, { headers: { Authorization: `Bearer ${tok()}` } }),
      ]);
      const tmiData = await tmiRes.json();
      if (!tmiRes.ok) throw new Error(tmiData.error || "Could not load your Team Member Information Sheet.");

      setOnboardingStage(tmiData.onboarding_stage || null);
      setUploadedDocs(tmiData.uploaded_documents || {});

      const tmi = tmiData.team_member_info;
      if (tmi) {
        const next = emptyForm();
        TMI_FIELDS.forEach((f) => {
          if (tmi[f] !== undefined && tmi[f] !== null) next[f] = tmi[f];
        });
        setForm(next);
        formRef.current = next;
        setSubmittedAt(tmi.submitted_at || null);
      }

      if (docsRes.ok) {
        const docsData = await docsRes.json();
        const filenames = {};
        (docsData.documents || []).forEach((d) => {
          if (!filenames[d.document_type]) filenames[d.document_type] = d.filename;
        });
        setDocFilenames(filenames);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedIn) load();
    else setLoading(false);
  }, []); // eslint-disable-line

  const updateField = (key, value) => {
    setForm((p) => {
      const next = { ...p, [key]: value };
      formRef.current = next;
      return next;
    });
  };

  const saveDraft = async () => {
    try {
      setSaveMsg("Saving…");
      const res = await fetch(`${API}/api/applicants/team-member-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(formRef.current),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not save");
      setSaveMsg(`Saved ${new Date().toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}`);
    } catch (e) {
      setSaveMsg(e.message || "Could not save — please check your connection.");
    }
  };

  // Debounced save — collapses rapid field-to-field tabbing into one PUT.
  const queueSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveDraft, 600);
  };

  const set = (key) => (e) => updateField(key, e.target.value);
  const setOnBlur = (key) => (e) => { updateField(key, e.target.value); queueSave(); };
  const setBoolOnChange = (key) => (e) => {
    const v = e.target.value;
    updateField(key, v === "" ? "" : v === "yes");
    queueSave();
  };

  const handleDocUpload = async (type, file) => {
    if (!file) return;
    setUploadStatus((p) => ({ ...p, [type]: "uploading" }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", type);
      const res = await fetch(`${API}/api/documents/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${tok()}` }, body: fd,
      });
      const d = await res.json();
      if (res.ok) {
        setUploadStatus((p) => ({ ...p, [type]: "done" }));
        setUploadedDocs((p) => ({ ...p, [type]: true }));
        setDocFilenames((p) => ({ ...p, [type]: d.document?.filename || file.name }));
      } else {
        setUploadStatus((p) => ({ ...p, [type]: "error" }));
      }
    } catch {
      setUploadStatus((p) => ({ ...p, [type]: "error" }));
    }
  };

  const handleSubmitForm = async () => {
    setSubmitError("");
    setSubmitting(true);
    try {
      const method = sigRef.current.getMethod();
      const signature_data = sigRef.current.getSignatureData();
      const res = await fetch(`${API}/api/applicants/team-member-info/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ method, signature_data }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not submit — please check every required field is filled in.");
      setSubmittedAt(new Date().toISOString());
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmitForm = declareChecked && !!sigRef.current?.canSubmit();

  // ── render ──

  if (!loggedIn) {
    return (
      <div style={{ minHeight: "100vh", background: BRAND.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "system-ui, sans-serif" }}>
        <div style={{ ...card, maxWidth: 420, textAlign: "center" }}>
          <p style={{ color: BRAND.textLight }}>Please log in via the Talent Pool portal to continue.</p>
          <a href={`${BASENAME}/portal`} style={{ color: BRAND.primary, fontWeight: 700 }}>← Go to Portal</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, fontFamily: "system-ui, sans-serif" }}>
      {/* Nav */}
      <div style={{ background: BRAND.primary, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: "-0.3px" }}>guud vision</div>
        <a href={`${BASENAME}/portal`} style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, textDecoration: "none" }}>← Back to Portal</a>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 16px" }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: BRAND.text, marginBottom: 4 }}>Team Member Information Sheet</h1>

        {loading && <p style={{ textAlign: "center", color: BRAND.textLight }}>Loading…</p>}

        {!loading && error && (
          <div style={{ ...card, background: BRAND.redBg, border: "1px solid #ef9a9a" }}>
            <p style={{ color: BRAND.red, margin: 0 }}>{error}</p>
          </div>
        )}

        {!loading && !error && submittedAt && (
          <div style={{ ...card, background: BRAND.greenBg, border: "1px solid #a5d6a7", textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <h2 style={{ margin: "0 0 6px", fontSize: 18, color: BRAND.green }}>Submitted</h2>
            <p style={{ margin: 0, color: BRAND.textLight, fontSize: 13 }}>
              Submitted on {new Date(submittedAt).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" })}. Thank you — no further action is needed here.
            </p>
          </div>
        )}

        {!loading && !error && !submittedAt && onboardingStage !== "team_member_info" && (
          <div style={{ ...card, background: "#fff8e1", border: "1px solid #ffe082" }}>
            <p style={{ margin: 0, color: "#8d6e00" }}>This step isn't available yet. Please check back once your admin has let you know it's time to fill this in.</p>
          </div>
        )}

        {!loading && !error && !submittedAt && onboardingStage === "team_member_info" && (
          <>
            <p style={{ color: BRAND.textLight, fontSize: 13, marginBottom: 16 }}>
              Please fill in every section below as completely and accurately as possible. Your progress is
              saved automatically as you go — you can leave and come back at any time before final submission.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 10 }}>
              {saveMsg && <span style={{ fontSize: 12, color: BRAND.textLight }}>{saveMsg}</span>}
              <button type="button" style={btnGhost} onClick={saveDraft}>Save Progress</button>
            </div>

            {/* Personal Information */}
            <div style={card}>
              <h3 style={cardTitle}>Personal Information</h3>
              <div style={gridTwo}>
                <label style={lbl}>Surname<input style={inp} value={form.surname} onChange={set("surname")} onBlur={queueSave} /></label>
                <label style={lbl}>First Names<input style={inp} value={form.first_names} onChange={set("first_names")} onBlur={queueSave} /></label>
                <label style={lbl}>Known As (AKA)<input style={inp} value={form.known_as} onChange={set("known_as")} onBlur={queueSave} /></label>
                <label style={lbl}>Second Name<input style={inp} value={form.second_name} onChange={set("second_name")} onBlur={queueSave} /></label>
                <label style={lbl}>Personal Title<input style={inp} value={form.personal_title} onChange={set("personal_title")} onBlur={queueSave} placeholder="e.g. Mr / Ms / Dr" /></label>
                <label style={lbl}>Initials<input style={inp} value={form.initials} onChange={set("initials")} onBlur={queueSave} /></label>
              </div>
              <label style={lbl}>Language<input style={inp} value={form.language} onChange={set("language")} onBlur={queueSave} /></label>
              <label style={lbl}>Residential Address *<textarea style={{ ...inp, resize: "vertical" }} rows={2} value={form.residential_address} onChange={set("residential_address")} onBlur={queueSave} /></label>
              <div style={gridTwo}>
                <label style={lbl}>Postal Code<input style={inp} value={form.postal_code} onChange={set("postal_code")} onBlur={queueSave} /></label>
                <label style={lbl}>Date of Birth *<input type="date" style={inp} value={form.date_of_birth} onChange={set("date_of_birth")} onBlur={queueSave} /></label>
                <label style={lbl}>Gender
                  <select style={inp} value={form.gender || ""} onChange={setOnBlur("gender")}>
                    <option value="">— Select —</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </label>
                <label style={lbl}>Race
                  <select style={inp} value={form.race || ""} onChange={setOnBlur("race")}>
                    <option value="">— Select —</option>
                    <option value="A">African</option>
                    <option value="W">White</option>
                    <option value="C">Coloured</option>
                    <option value="I">Indian</option>
                  </select>
                </label>
                <label style={lbl}>Home Telephone<input style={inp} value={form.home_telephone} onChange={set("home_telephone")} onBlur={queueSave} /></label>
                <label style={lbl}>Cell Phone *<input style={inp} value={form.cell_phone} onChange={set("cell_phone")} onBlur={queueSave} /></label>
                <label style={lbl}>Income Tax Number<input style={inp} value={form.income_tax_number} onChange={set("income_tax_number")} onBlur={queueSave} /></label>
                <label style={lbl}>Personal Email<input type="email" style={inp} value={form.personal_email} onChange={set("personal_email")} onBlur={queueSave} /></label>
              </div>
              <label style={lbl}>Driver's License
                <select style={inp} value={boolSelectValue(form.has_drivers_license)} onChange={setBoolOnChange("has_drivers_license")}>
                  <option value="">— Select —</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              {form.has_drivers_license === true && (
                <label style={lbl}>Licence Code<input style={inp} value={form.drivers_license_code} onChange={set("drivers_license_code")} onBlur={queueSave} /></label>
              )}
            </div>

            {/* Emergency Medical Information */}
            <div style={card}>
              <h3 style={cardTitle}>Emergency Medical Information</h3>
              <div style={gridTwo}>
                <label style={lbl}>Name of Doctor<input style={inp} value={form.doctor_name} onChange={set("doctor_name")} onBlur={queueSave} /></label>
                <label style={lbl}>Doctor's Telephone #<input style={inp} value={form.doctor_telephone} onChange={set("doctor_telephone")} onBlur={queueSave} /></label>
              </div>
              <label style={lbl}>Allergies &amp; Medical Conditions<textarea style={{ ...inp, resize: "vertical" }} rows={2} value={form.allergies_medical_conditions} onChange={set("allergies_medical_conditions")} onBlur={queueSave} /></label>
              <div style={gridTwo}>
                <label style={lbl}>Contact in Emergencies (name)<input style={inp} value={form.emergency_contact_name} onChange={set("emergency_contact_name")} onBlur={queueSave} /></label>
                <label style={lbl}>Contact Telephone #<input style={inp} value={form.emergency_contact_telephone} onChange={set("emergency_contact_telephone")} onBlur={queueSave} /></label>
              </div>
            </div>

            {/* Next of Kin */}
            <div style={card}>
              <h3 style={cardTitle}>Next of Kin</h3>
              <div style={gridTwo}>
                <label style={lbl}>Full Name<input style={inp} value={form.next_of_kin_name} onChange={set("next_of_kin_name")} onBlur={queueSave} /></label>
                <label style={lbl}>Relationship<input style={inp} value={form.next_of_kin_relationship} onChange={set("next_of_kin_relationship")} onBlur={queueSave} /></label>
              </div>
              <label style={lbl}>Residential Address<textarea style={{ ...inp, resize: "vertical" }} rows={2} value={form.next_of_kin_address} onChange={set("next_of_kin_address")} onBlur={queueSave} /></label>
              <div style={gridTwo}>
                <label style={lbl}>Postal Code<input style={inp} value={form.next_of_kin_postal_code} onChange={set("next_of_kin_postal_code")} onBlur={queueSave} /></label>
                <label style={lbl}>Home Telephone #<input style={inp} value={form.next_of_kin_home_telephone} onChange={set("next_of_kin_home_telephone")} onBlur={queueSave} /></label>
                <label style={lbl}>Cell #<input style={inp} value={form.next_of_kin_cell_phone} onChange={set("next_of_kin_cell_phone")} onBlur={queueSave} /></label>
              </div>
            </div>

            {/* Residential / Passport Information */}
            <div style={card}>
              <h3 style={cardTitle}>Residential / Passport Information</h3>
              <label style={lbl}>Are you a SA Citizen?
                <select style={inp} value={boolSelectValue(form.is_sa_citizen)} onChange={setBoolOnChange("is_sa_citizen")}>
                  <option value="">— Select —</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              {form.is_sa_citizen === false && (
                <div style={gridTwo}>
                  <label style={lbl}>Work Permit Number<input style={inp} value={form.work_permit_number} onChange={set("work_permit_number")} onBlur={queueSave} /></label>
                  <label style={lbl}>Date Expired<input type="date" style={inp} value={form.work_permit_expiry_date} onChange={set("work_permit_expiry_date")} onBlur={queueSave} /></label>
                </div>
              )}
            </div>

            {/* Banking Details */}
            <div style={card}>
              <h3 style={cardTitle}>Banking Details for Payroll Purposes</h3>
              <div style={gridTwo}>
                <label style={lbl}>Name of Bank *<input style={inp} value={form.bank_name} onChange={set("bank_name")} onBlur={queueSave} /></label>
                <label style={lbl}>Branch<input style={inp} value={form.bank_branch} onChange={set("bank_branch")} onBlur={queueSave} /></label>
                <label style={lbl}>Account Number *<input style={inp} value={form.bank_account_number} onChange={set("bank_account_number")} onBlur={queueSave} /></label>
                <label style={lbl}>Branch Code<input style={inp} value={form.bank_branch_code} onChange={set("bank_branch_code")} onBlur={queueSave} /></label>
                <label style={lbl}>Account Holder<input style={inp} value={form.bank_account_holder} onChange={set("bank_account_holder")} onBlur={queueSave} /></label>
                <label style={lbl}>Account Type
                  <select style={inp} value={form.bank_account_type || ""} onChange={setOnBlur("bank_account_type")}>
                    <option value="">— Select —</option>
                    <option value="transmission">Transmission</option>
                    <option value="current">Current</option>
                    <option value="savings">Savings</option>
                  </select>
                </label>
              </div>
            </div>

            {/* Supporting Documents */}
            <div style={card}>
              <h3 style={cardTitle}>Supporting Documents</h3>
              {SUPPORTING_DOCS.map(({ type, label }) => (
                <div key={type} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 0", borderBottom: `1px solid ${BRAND.border}` }}>
                  <span style={{ fontSize: 13, color: BRAND.text }}>{label}</span>
                  {uploadedDocs[type] ? (
                    <span style={{ fontSize: 13, color: BRAND.green, fontWeight: 600 }}>
                      ✅ {docFilenames[type] || "Uploaded"}
                    </span>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        style={{ fontSize: 12 }}
                        onChange={(e) => handleDocUpload(type, e.target.files?.[0])}
                      />
                      {uploadStatus[type] === "uploading" && <span style={{ fontSize: 12, color: BRAND.textLight }}>Uploading…</span>}
                      {uploadStatus[type] === "error" && <span style={{ fontSize: 12, color: BRAND.red }}>✗ Error</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Declaration + Signature */}
            <div style={card}>
              <h3 style={cardTitle}>Team Member Declaration</h3>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16, cursor: "pointer", fontSize: 13, color: BRAND.text }}>
                <input type="checkbox" checked={declareChecked} onChange={(e) => setDeclareChecked(e.target.checked)} style={{ marginTop: 2 }} />
                <span>I declare that the above information is true.</span>
              </label>

              <SignaturePad ref={sigRef} />

              {submitError && <p style={{ color: BRAND.red, fontSize: 13, marginTop: 12 }}>{submitError}</p>}

              <button
                type="button"
                style={{ ...btnP(!canSubmitForm || submitting), marginTop: 16, width: "100%" }}
                disabled={!canSubmitForm || submitting}
                onClick={handleSubmitForm}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
